import React, { FC } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import isNil from 'lodash/isNil';
import moment from 'moment';

import { IUpsertOverrideItemRequestDTO } from 'src/api/dtos';
import { AddIcon, Checkbox, EditIcon, SingleDatePicker, TrashIcon } from 'src/shared/components';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { OperationTypeEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithFade, ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';

import { IAvailabilityForm } from './availabilities.types';
import { EmployeeAvailabilityEditTable } from './EmployeeAvailabilityEditTable';
import { useEmployeeAvailabilitySingleDay } from './providers/EmployeeAvailabilitySingleDayProvider';

export const EmployeeAvailabilitySingleDaySection: FC = () => {
  const { t } = useTranslate();

  const {
    isOverride,
    isRecurring,
    values,
    touched,
    errors,
    apiError,
    isEditMode,
    date,
    showSingleDayAvailability,
    loading,
    onSetEditMode,
    onValidateSubmit,
    onCancel,
    onChangeDate,
    onChangeFullDay,
    onChangeStatus,
    onDeleteOverride,
    onAddOverride,
    onChangeStartTime,
    onChangeEndTime,
    onCheckOtherDates,
    onChangeOtherDate,
    onDeleteOtherDate,
    onAddOtherDate,
  } = useEmployeeAvailabilitySingleDay();

  const isFullDay = !values.overrides.some(
    ({ startTimeInSeconds, endTimeInSeconds, operationType }) =>
      (!isNil(startTimeInSeconds) || !isNil(endTimeInSeconds)) && operationType !== OperationTypeEnums.DELETE,
  );

  const isEditModeAllowed = !isEditMode && date?.isSameOrAfter(moment(), 'days');

  return (
    <Stack direction="column" gap={4}>
      {apiError && <Alert severity="error">{apiError}</Alert>}

      <ComponentWithFade show={showSingleDayAvailability}>
        {isOverride && <Alert severity="info">{t('availability.theAvailabilityIsOverwrittenForThisDate')}</Alert>}
        {isRecurring && <Alert severity="info">{t('availability.theAvailabilityForThisDateIsSetAsPartOf')}</Alert>}
      </ComponentWithFade>

      <Stack direction="column" sx={{ maxWidth: '240px' }}>
        <label className="small mb-2 fw-medium d-block">{t('global.date')}</label>
        <SingleDatePicker
          minDate={moment()}
          id="startDate"
          value={date ? moment(date) : null}
          onChange={onChangeDate}
          placeholder={t('absences.selectDate')}
        />
      </Stack>

      <ComponentWithFade show={showSingleDayAvailability}>
        <Stack direction="column" gap={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontSize="1.25rem" color="text.secondary">
              {t('availability.singleDay')}
            </Typography>

            <ComponentWithPermissions permissions={PermissionsEnums.WORK_TIME_OVERRIDES_EDIT}>
              {isEditModeAllowed && (
                <IconButton onClick={onSetEditMode} disabled={loading}>
                  <EditIcon size={20} />
                </IconButton>
              )}
            </ComponentWithPermissions>
          </Stack>

          {loading && <Skeleton className="rounded" variant="rectangular" animation="wave" height="140px" />}

          {!loading && !values.overrides.length && (
            <Box marginBottom={4}>
              <Typography variant="body2" color="text.secondary">
                {t('availability.notSpecified')}
              </Typography>
            </Box>
          )}

          {!loading && !!values.overrides.length && (
            <EmployeeAvailabilityEditTable
              isEditMode={isEditMode}
              isFullDay={isFullDay}
              availabilities={mapOverridesToFormData(values.overrides)}
              getErrors={(index, key) => {
                const error = errors.overrides?.[index];
                const overrideError = !!error && typeof error !== 'string' ? error : undefined;
                return overrideError?.[key];
              }}
              getTouched={(index) => (typeof touched.overrides === 'object' ? !!touched.overrides?.[index] : false)}
              onChangeFullDay={onChangeFullDay}
              onChangeStatus={onChangeStatus}
              onDelete={onDeleteOverride}
              onAdd={onAddOverride}
              onChangeStartTime={onChangeStartTime}
              onChangeEndTime={onChangeEndTime}
            />
          )}

          {isEditMode && (
            <Stack direction="column" gap={3}>
              {!!values.overrides.length && (
                <Stack direction="column" gap={2} paddingLeft={1}>
                  <Checkbox
                    _container={{ alignSelf: 'flex-start' }}
                    className="text-muted"
                    label={<Typography variant="body2">{t('availability.copyAvailabilityToOtherDates')}</Typography>}
                    checked={!!values.otherDates?.length}
                    onCheck={() => onCheckOtherDates()}
                    disabled={loading}
                  />

                  {values.otherDates?.map((otherDate, index, list) => {
                    const errorMessage = errors.otherDates?.[index];
                    const otherDatesTouched =
                      typeof touched.otherDates === 'object' ? touched.otherDates?.[index] : false;
                    const showError = otherDatesTouched && !!errorMessage;

                    return (
                      <Stack key={`other-date-${index}`} gap={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {t('availability.day')} {index + 1}
                        </Typography>
                        <Stack direction="row" alignItems={showError ? 'flex-start' : 'center'} gap={0.5}>
                          <SingleDatePicker
                            minDate={moment()}
                            sx={{ width: 'fit-content' }}
                            shouldDisableDate={(calendarDate) => isDateDisabled(calendarDate, list, date)}
                            error={showError ? errorMessage : undefined}
                            value={otherDate.length ? moment(otherDate) : null}
                            onChange={(date) => onChangeOtherDate(date, index)}
                            placeholder={t('absences.selectDate')}
                          />
                          <IconButton color="error" onClick={() => onDeleteOtherDate(index)}>
                            <TrashIcon size={20} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    );
                  })}

                  {!!values.otherDates?.length && (
                    <Button
                      sx={{ width: 'fit-content' }}
                      startIcon={<AddIcon />}
                      onClick={onAddOtherDate}
                      color="secondary"
                      disabled={loading}
                    >
                      {t('availability.addDate')}
                    </Button>
                  )}
                </Stack>
              )}

              <Stack direction="row" alignItems="center" gap={2}>
                <Button color="secondary" onClick={onCancel}>
                  {t('global.cancel')}
                </Button>
                <Button variant="contained" onClick={onValidateSubmit} disabled={loading}>
                  {t('global.save')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </ComponentWithFade>
    </Stack>
  );
};

const mapOverridesToFormData = (overrides: IUpsertOverrideItemRequestDTO[]): IAvailabilityForm[] => {
  return overrides.map((override) => ({
    id: override.id,
    status: override.status,
    startTimeInSeconds: override.startTimeInSeconds,
    endTimeInSeconds: override.endTimeInSeconds,
    operationType: override.operationType,
  }));
};

/**
 * Validates if the calendar date should be disabled if it is the same as a date in other dates list or selected date
 */
const isDateDisabled = (calendarDate: moment.Moment, otherDates: string[], selectedDate: moment.Moment | null) => {
  return (
    moment(calendarDate).isSame(selectedDate, 'day') ||
    otherDates.some((date) => moment(date, API_DATE_FORMAT).isSame(calendarDate, 'day'))
  );
};
